import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Building2, Wallet, AlertTriangle, Clock, FileSignature, Coins,
    Shield, ArrowUpRight, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLE_LABELS } from "../lib/roles";
import { Button } from "../components/ui/button";

const SEMAFORO = {
    verde:  { chip: "badge-exito",  border: "border-l-4 border-l-[color:var(--brand-exito)]" },
    gold:   { chip: "badge-gold",   border: "border-l-4 border-l-[color:var(--brand-dark-gold)]" },
    rojo:   { chip: "badge-error",  border: "border-l-4 border-l-[color:var(--brand-coral)]" },
    negro:  { chip: "badge-error",  border: "border-l-4 border-l-[color:var(--brand-charcoal)]" },
};

function fmtMoney(n, cur = "MXN") {
    return (n || 0).toLocaleString("es-MX", { style: "currency", currency: cur, maximumFractionDigits: 0 });
}

export default function Dashboard() {
    const { user, hasRole } = useAuth();
    const [kpis, setKpis] = useState(null);
    const [matriz, setMatriz] = useState([]);
    const [timeline, setTimeline] = useState({});
    const [bit, setBit] = useState([]);
    const [loading, setLoading] = useState(true);
    const puedeVerBitagg = hasRole(["auditor", "direccion", "admin_ti"]);

    useEffect(() => {
        (async () => {
            try {
                const [k, m, t] = await Promise.all([
                    api.get("/dashboard/kpis"),
                    api.get("/dashboard/matriz-fideicomisos"),
                    api.get("/dashboard/timeline-obligaciones"),
                ]);
                setKpis(k.data); setMatriz(m.data); setTimeline(t.data?.por_semana || {});
                if (puedeVerBitagg) {
                    const b = await api.get("/dashboard/bitacora-agregada?limit=15");
                    setBit(b.data);
                }
            } catch (e) {
                toast.error("Error cargando dashboard");
            } finally { setLoading(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedMatriz = useMemo(() => {
        const order = { negro: 0, rojo: 1, gold: 2, verde: 3 };
        return [...matriz].sort((a, b) => (order[a.semaforo] ?? 9) - (order[b.semaforo] ?? 9));
    }, [matriz]);

    if (loading) return <div className="p-10 text-sm text-muted-foreground">Cargando panel de control…</div>;

    const totalPatrimonioMxn = (kpis?.patrimonio_por_moneda?.MXN || 0);
    const totalPatrimonioUsd = (kpis?.patrimonio_por_moneda?.USD || 0);
    const semanasOrdenadas = Object.keys(timeline).sort();

    return (
        <div className="p-8 lg:p-10 space-y-8 max-w-[1600px]" data-testid="dashboard-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="max-w-2xl">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Panel de control · Multi-fideicomiso
                    </div>
                    <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                        {(user?.nombre || "").split(" ")[0] || "Hola"}, tu radar operativo{" "}
                        <span className="text-[color:var(--brand-coral)]">al día</span>.
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground max-w-xl">
                        {kpis?.alcance === "todos"
                            ? "Vista completa · todos los fideicomisos"
                            : "Vista limitada a los fideicomisos que administras"}
                    </p>
                </div>
                <div className="kpi-card min-w-[260px]">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Sesión activa
                    </div>
                    <div className="text-sm font-medium mt-1 truncate">{user?.email}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {(user?.roles || []).slice(0, 3).map((r) => (
                            <span key={r} className="badge-gold text-[10px]">{ROLE_LABELS[r] || r}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* KPIs principales */}
            <div className="grid md:grid-cols-4 gap-4">
                <div className="kpi-card" data-testid="dashboard-kpi-total_fideicomisos">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Building2 className="h-3.5 w-3.5" /> Fideicomisos
                    </div>
                    <div className="kpi-value mt-2">{kpis?.fideicomisos_totales || 0}</div>
                    <div className="mt-2 text-xs text-muted-foreground space-x-2">
                        {Object.entries(kpis?.por_ciclo || {}).map(([k, v]) => (
                            <span key={k}>{k}: <b>{v}</b></span>
                        ))}
                    </div>
                </div>
                <div className="kpi-card" data-testid="dashboard-kpi-patrimonio">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Wallet className="h-3.5 w-3.5" /> Patrimonio administrado
                    </div>
                    <div className="kpi-value mt-2 text-3xl">{fmtMoney(totalPatrimonioMxn)}</div>
                    {totalPatrimonioUsd > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                            + {fmtMoney(totalPatrimonioUsd, "USD")}
                        </div>
                    )}
                </div>
                <div className="kpi-card" data-testid="dashboard-kpi-instrucciones_pendientes">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <FileSignature className="h-3.5 w-3.5" /> Instrucciones pendientes
                    </div>
                    <div className="kpi-value mt-2">{kpis?.instrucciones_pendientes || 0}</div>
                    <Link to="/instrucciones" className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--brand-dark-gold)] hover:underline">
                        Ver flujo <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className={`kpi-card ${(kpis?.obligaciones_vencidas || 0) > 0 ? "border-[color:var(--brand-coral)]/50" : ""}`}
                    data-testid="dashboard-kpi-obligaciones_vencidas">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <AlertTriangle className="h-3.5 w-3.5" /> Obligaciones vencidas
                    </div>
                    <div className={`kpi-value mt-2 ${(kpis?.obligaciones_vencidas || 0) > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                        {kpis?.obligaciones_vencidas || 0}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Próx. 30d: <b>{kpis?.obligaciones_proximas_30d || 0}</b>
                    </div>
                </div>
            </div>

            {/* Alertas y honorarios */}
            <div className="grid md:grid-cols-3 gap-4">
                <div className="kpi-card" data-testid="dashboard-kpi-honorarios_por_cobrar">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Coins className="h-3.5 w-3.5" /> Honorarios por cobrar
                    </div>
                    <div className="kpi-value mt-2 text-2xl">
                        {fmtMoney(kpis?.honorarios_por_cobrar?.monto_pendiente || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {kpis?.honorarios_por_cobrar?.cantidad || 0} facturas ·
                        mora prom.: <b>{kpis?.honorarios_por_cobrar?.dias_mora_promedio || 0} días</b>
                    </div>
                </div>
                <div className="kpi-card" data-testid="dashboard-kpi-kyc_alertas">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Shield className="h-3.5 w-3.5" /> Alertas KYC / cuentas
                    </div>
                    <div className="mt-2 flex gap-6 items-baseline">
                        <div>
                            <div className={`text-2xl font-display ${(kpis?.kyc_vencidos_sin_override || 0) > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                                {kpis?.kyc_vencidos_sin_override || 0}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">KYC vencidos</div>
                        </div>
                        <div>
                            <div className={`text-2xl font-display ${(kpis?.cuentas_congeladas || 0) > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                                {kpis?.cuentas_congeladas || 0}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cuentas congeladas</div>
                        </div>
                    </div>
                </div>
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <TrendingUp className="h-3.5 w-3.5" /> Actividad reciente
                    </div>
                    <div className="text-sm mt-2 text-muted-foreground">
                        Última acción del sistema hace pocos minutos.
                    </div>
                    {puedeVerBitagg && (
                        <Link to="/bitacora" className="mt-3 inline-flex items-center gap-1 text-xs text-[color:var(--brand-dark-gold)] hover:underline">
                            Ver bitácora <ArrowUpRight className="h-3 w-3" />
                        </Link>
                    )}
                </div>
            </div>

            {/* Matriz de fideicomisos */}
            <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="matriz-fideicomisos">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-lg">Matriz de fideicomisos</h2>
                        <p className="text-[11px] text-muted-foreground">Ordenados por criticidad · click para abrir ficha</p>
                    </div>
                    <div className="text-[10px] flex gap-2 items-center text-muted-foreground">
                        <span className="badge-error">Crítico</span>
                        <span className="badge-gold">Próximo</span>
                        <span className="badge-exito">OK</span>
                    </div>
                </div>
                <table className="trf-table">
                    <thead><tr>
                        <th></th><th>Interno</th><th>Tipo</th><th>Estado</th>
                        <th className="text-right">Completitud</th>
                        <th className="text-right">Oblig. vencidas</th>
                        <th className="text-right">Próximas 30d</th>
                        <th>Última acción</th><th></th>
                    </tr></thead>
                    <tbody>
                        {sortedMatriz.map((f) => {
                            const sem = SEMAFORO[f.semaforo] || SEMAFORO.verde;
                            return (
                                <tr key={f.id} className={sem.border} data-testid={`matriz-row-${f.id}`}>
                                    <td>
                                        <span className={sem.chip}>{f.semaforo}</span>
                                    </td>
                                    <td className="font-medium">{f.numero_interno}</td>
                                    <td className="capitalize">{f.tipo}</td>
                                    <td className="text-xs">
                                        <span className={f.estado_ciclo === "activo" ? "badge-exito" : "badge-gold"}>
                                            {f.estado_ciclo}
                                        </span>
                                        {f.servicios_suspendidos && (
                                            <span className="badge-error ml-1">suspendido</span>
                                        )}
                                    </td>
                                    <td className="text-right tabular">
                                        {f.completitud_pct != null ? `${f.completitud_pct}%` : "—"}
                                    </td>
                                    <td className={`text-right tabular ${f.obligaciones_vencidas > 0 ? "text-[color:var(--brand-coral)] font-bold" : ""}`}>
                                        {f.obligaciones_vencidas}
                                    </td>
                                    <td className="text-right tabular">{f.obligaciones_proximas}</td>
                                    <td className="text-[10px] text-muted-foreground tabular">
                                        {f.ultima_actividad ? f.ultima_actividad.substring(0, 16) : "—"}
                                        {f.alert_constitucion_larga && (
                                            <div className="text-[color:var(--brand-coral)] text-[10px]">&gt;30d en constitución</div>
                                        )}
                                    </td>
                                    <td>
                                        <Link to={`/fideicomisos/${f.id}`}
                                            data-testid={`drill-down-${f.id}`}
                                            className="text-xs text-[color:var(--brand-dark-gold)] hover:underline">
                                            Abrir →
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Timeline + Bitácora agregada */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="rounded-md border border-border bg-card" data-testid="timeline-obligaciones">
                    <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <h2 className="font-display text-lg flex items-center gap-2">
                                <Clock className="h-4 w-4 text-[color:var(--brand-dark-gold)]" /> Timeline 30 días
                            </h2>
                            <p className="text-[11px] text-muted-foreground">Obligaciones por semana ISO</p>
                        </div>
                    </div>
                    <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                        {semanasOrdenadas.length === 0 && (
                            <div className="text-xs text-muted-foreground text-center py-6">Sin obligaciones próximas.</div>
                        )}
                        {semanasOrdenadas.map((sem) => (
                            <div key={sem} className="rounded-sm border border-border/60 p-3">
                                <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand-dark-gold)] font-semibold mb-1">
                                    Semana {sem} — {timeline[sem].length} pendientes
                                </div>
                                {timeline[sem].map((o) => {
                                    const s = SEMAFORO[o.semaforo] || SEMAFORO.verde;
                                    return (
                                        <div key={o.id} className="flex items-center gap-2 py-1 text-xs">
                                            <span className={s.chip}>{o.tipo}</span>
                                            <span className="flex-1 truncate">{o.titulo}</span>
                                            <span className="tabular text-muted-foreground">{o.fecha_limite}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                {puedeVerBitagg && (
                    <div className="rounded-md border border-border bg-card" data-testid="bitacora-agregada">
                        <div className="px-5 py-3 border-b border-border">
                            <h2 className="font-display text-lg flex items-center gap-2">
                                <Shield className="h-4 w-4 text-[color:var(--brand-dark-gold)]" /> Bitácora agregada
                            </h2>
                            <p className="text-[11px] text-muted-foreground">Últimas 15 acciones cross-trust</p>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                            <table className="trf-table text-xs">
                                <thead><tr>
                                    <th>Fecha</th><th>Usuario</th><th>Acción</th><th>Entidad</th>
                                </tr></thead>
                                <tbody>
                                    {bit.map((b) => (
                                        <tr key={b.id}>
                                            <td className="tabular text-[10px]">{b.timestamp?.substring(0, 16)}</td>
                                            <td className="text-[10px]">{b.user_email?.split("@")[0]}</td>
                                            <td className="font-mono text-[10px]">{b.action}</td>
                                            <td className="text-[10px]">{b.entity_type}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
